<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <export-sidebar
          sidebar-id="sidebar-invoices-export"
          storage-key="sidebar_invoices_export"
          :columns="exportableFields"
          :data-to-export="dataToExport"
          :data-mapper="mapInvoiceForExport"
          file-name="Invoices.xlsx"
          sheet-name="Invoices"
          :is-busy="isBusy"
          :items-count="totalRows"
        />
        <b-button id="table-settings" size="sm" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-button class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый счёт
        </b-button>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Период</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Организация</label>
            <organization-select
              id="input-organization-select"
              ref="organization_select"
              :value="filters.org_id"
              @onSelect="onSelectOrganization"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Заказчик</label>
            <customer-select
              id="input-customer-select"
              ref="customer_select"
              :value="filters.customer_id"
              @onSelect="onSelectCustomer"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Статус оплаты</label>
            <b-form-select
              id="input-statuses"
              size="sm"
              v-model="filters.paid_type"
            >
              <option :value="-1">Все статусы</option>
              <option v-for="option in paidTypes" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="invoices"
            ref="invoices"
            class="custom-table mb-0 text-small"
            sticky-header
            :tbody-tr-class="tbodyRowClass"
            select-mode="multi"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-clicked="onRowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="4"/>
            </template>

            <template #cell(show_details)="row" >
              <b-button v-if="row.item.works.length > 0" variant="link" size="sm" @click="row.toggleDetails" class="">
                <font-awesome-icon v-if="row.detailsShowing" icon="chevron-up" />
                <font-awesome-icon v-else icon="chevron-down" />
              </b-button>
            </template>

            <template #row-details="row">
              <b-table :items="row.item.works" :fields="worksFields" small class="w-auto">
                <template #cell(unit)="row">
                  {{getUnitByCode(row.item.unit_code).shortName}}
                </template>
                <template #custom-foot="data">
                  <b-tr>
                    <b-th colspan="5" class="text-right">
                      Итого:<br>
                      {{getTotalWorksSumText(row.item)}}
                    </b-th>
                    <b-th>{{priceFormat(getTotalWorksSum(row.item))}}</b-th>
                  </b-tr>
                </template>
              </b-table>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                size="md"
                @change="selectPageRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                class="mx-1"
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowClicked(row.item)"
              />
            </template>

            <template #cell(date)="row">
              {{formatTableDate(row.item.date)}}
            </template>

            <template #cell(customer_name)="row">
              {{row.item.customer_name}}
              <p class="m-0 p-0 text-secondary text-small">{{row.item.organization_name}}</p>
            </template>

            <template #cell(paid_type)="row">
              <span :class="['custom-tag', getStatusStyle(row.item.paid_type)]" @click.stop="showModalPayments(row.item)">
                {{ getStatusName(row.item.paid_type) }}
                <v-icon name="buttons/payment" :size="12" class="ml-1"/>
              </span>
            </template>

            <template #cell(acts)="row">
              <b-list-group flush>
                <b-list-group-item style="background-color: inherit; padding: 0" v-for="(item, index) in row.item.acts" :key="index">
                  <b-button size="sm" variant="link" @click="onActFile(item)">Акт №{{ item.numb }}</b-button>
                </b-list-group-item>
              </b-list-group>
            </template>

            <template #cell(summa)="row">
              {{priceFormat(row.item.summa)}}
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" class="custom-dropdown" no-caret>
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click="showModalPayments(row.item)">
                  <v-icon name="buttons/payment" :size="14" class="mr-2"/>
                  Оплатить
                </b-dropdown-item>
                <b-dropdown-item variant="info" @click="onInvoiceFile(row.item)">
                  <v-icon name="buttons/export" :size="14" class="mr-2"/>
                  Скачать
                </b-dropdown-item>
                <b-dropdown-item variant="primary" @click="onEdit(row)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            count-right
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <div class="text-small">
                Найдено <b>{{totalRows}}</b> счетов на сумму <b>{{total.total_sum}}</b>
                из них оплачено <b>{{total.paid_count}}</b> на сумму <b>{{total.paid_sum}}</b> руб.,
                не оплачено <b>{{total.unpaid_count}}</b> на сумму <b>{{total.unpaid_sum}}</b> руб.,
                частично оплачено <b>{{total.partial_count}}</b> на сумму <b>{{total.partial_sum}}</b> руб.
                <span v-if="selected.length > 0" class="text-small vertical-divider"/>
              </div>
            </template>
            <template #count-right>
              <span v-if="selected.length > 0" class="text-small mr-2">
                Выбрано: <b>{{ selected.length }}</b>
              </span>
              <span v-if="selected.length > 0" class="vertical-divider"/>
              <span class="text-small text-muted">Записей: <b>{{ totalRows }}</b></span>
              <span class="vertical-divider"/>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных счётов.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новый счёт прямо сейчас</p>
          <b-btn :disabled="isBusy" class="custom-btn-primary mt-1" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый счёт
          </b-btn>
        </div>
      </div>
    </div>

    <invoices-payments ref="invoicesPayments" @update="onUpdateInvoicePayments"></invoices-payments>

    <DynamicTables
      storage-key="invoices_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="numb"
    />
  </div>
</template>

<script>
import invoices from "../../network/invoices";
import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import unitStore from "../../utilites/unitStore";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import {parse} from "content-disposition-attachment";
  import {rubles} from 'rubles';
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import OrganizationSelect from "../Organizations/OrganizationSelect";
    import CustomerSelect from "../Customers/CustomerSelect";
    import InvoicesPayments from "./InvoicesPayments.vue";
    import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";

export default {
  name: "Invoices",
  components: { TablesSkeleton, ExportSidebar, PaginationFooter, FiltersDrawer, DynamicTables, CustomerSelect, OrganizationSelect, InvoicesPayments },
  mixins: [
    invoices,
    helpers, dateFormatter, unitStore, pageSearchService
  ],
  data() {
    return {
      title: "Выставление счёта",
      isBusy: false,
      items: [],
      serviceStartField: [
        {
          key: 'show_details',
          label: '',
          thStyle: { width: '40px', 'min-width': '40px' },
          class: 'text-center',
        },
        {
          key: 'selected',
          label: '',
          thStyle: { width: '50px', 'min-width': '50px' },
        }
      ],
      fields: [
        {
          key: 'numb',
          label: '№',
          thStyle: { width: '60px', 'min-width': '60px' },
          class: 'text-center font-weight-bold',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'date',
          label: "Дата",
          thStyle: { width: '80px', 'min-width': '80px' },
          class: 'text-center',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'customer_name',
          label: "Покупатель",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'paid_type',
          label: "Оплата",
          thStyle: { width: '150px', 'min-width': '150px' },
          class: 'text-center',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'acts',
          label: "Акты",
          thStyle: { width: '120px', 'min-width': '120px' },
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'summa',
          label: "Сумма",
          thStyle: { width: '150px', 'min-width': '150px' },
          class: 'text-center',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'payment_sum',
          label: "Оплачено",
          thStyle: { width: '150px', 'min-width': '150px' },
          class: 'text-center',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'remainder_sum',
          label: "Остаток",
          thStyle: { width: '150px', 'min-width': '150px' },
          class: 'text-center',
          sortable: true,
          visible: true,
          exportable: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: "Действия",
        thStyle: { width: '90px', 'min-width': '90px' },
        class: 'text-center',
      },
      additionalExportFields: [
        {
          key: 'organization_name',
          label: "Организация",
          visible: true,
          exportable: true,
        },
      ],
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      total: {
        total_sum: 0,
        paid_count: 0,
        paid_sum: 0,
        unpaid_count: 0,
        unpaid_sum: 0,
        partial_count: 0,
        partial_sum: 0,
      },
      deleteRow: {
        id: null,
        customer_name: ''
      },
      paidTypes: [
        { id: 0, name: "Не оплачено" },
        { id: 1, name: "Оплачено" },
        { id: 2, name: "Частично оплачено" },
        { id: 3, name: "Переплата"}
      ],
      worksFields: [
        {
          key: 'name',
          label: 'Наименование',
        },
        {
          key: 'price',
          label: 'Цена',
          class: 'widthPrice',
        },
        {
          key: 'count',
          label: 'Количество',
          class: 'widthPrice',
        },
        {
          key: 'unit',
          label: 'Ед. изм.',
          class: 'widthPrice',
        },
        {
          key: 'nds',
          label: 'НДС',
          class: 'widthPrice',
        },
        {
          key: 'sum',
          label: 'Сумма',
          class: 'widthPrice',
        },
      ],
      totalWorks: {
        total: 0,
        sum: 0
      },
      selected: [],
      allSelected: false,
      indeterminate: false,
    }
  },
  async created() {
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.start_date) {
        this.filters.start_date = query.start_date;
      }

      if (query.end_date) {
        this.filters.end_date = query.end_date;
      }

      if (query.paid_type) {
        this.filters.paid_type = query.paid_type;
      }

      if (query.org_id) {
        this.filters.org_id = query.org_id;
      }

      if (query.customer_id) {
        this.filters.customer_id = query.customer_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  async mounted() {
    await this.configureRefs();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.items;
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];
      return this.handleFixedCols(allFields);
    },
    exportableFields() {
      return [...this.fields, ...this.additionalExportFields]
        .filter(field => field.exportable)
        .map(field => ({
          key: field.key,
          label: field.label,
          visible: field.visible,
        }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.org_id !== defaults.org_id) {
        tags.push({ key: 'organization', label: `Организация`});
      }

      if (this.filters.customer_id !== defaults.customer_id) {
        tags.push({ key: 'customer', label: `Заказчик`,});
      }

      if (this.filters.paid_type !== defaults.paid_type) {
        const foundItem = this.paidTypes.find(s => s.id === this.filters.paid_type);
        tags.push({ key: 'paid', label: foundItem ? foundItem.name : 'Статус оплаты' });
      }

      return tags;
    },
    isFiltered() {
      const hasQuery = !!this.filters.query && this.filters.query.trim().length > 0;
      const hasTags = this.activeFilterTags.length > 0;

      return hasQuery || hasTags;
    },
  },
  watch: {
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.items.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    onCreate() {
      this.$router.push({name: 'InvoiceAdd'}).catch(()=>{});
    },
    onEdit(row) {
      this.$router.push({name: 'InvoiceEdit', params: { invoice_id: row.item.id }}).catch(()=>{});
    },
    onRowDblClicked(item) {
      this.$router.push({name: 'InvoiceEdit', params: { invoice_id: item.id }}).catch(()=>{});
    },
    async configureRefs() {
      if (this.$refs.organization_select) {
        await this.$refs.organization_select.configure(this.filters.org_id);
      }

      if (this.$refs.customer_select) {
        await this.$refs.customer_select.configure(this.filters.customer_id);
      }
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        start_date: null,
        end_date: null,
        paid_type: -1,
        org_id: 0,
        customer_id: 0,
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;

      this.$refs.organization_select.onClear();
      this.$refs.customer_select.onClear();
      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = null;
        this.filters.end_date = null;
      }
      else if (tag.key === 'organization') {
        this.$refs.organization_select.onClear();
        this.filters.org_id = 0;
      }
      else if (tag.key === 'customer') {
        this.$refs.customer_select.onClear();
        this.filters.customer_id = 0;
      }
      else if (tag.key === 'paid') {
        this.filters.paid_type = -1;
      }
      this.onSearch();
    },
    onSelectOrganization(id) {
      this.filters.org_id = id;
    },
    onSelectCustomer(id) {
      this.filters.customer_id = id;
    },
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        paid_type: this.filters.paid_type,
        org_id: this.filters.org_id,
        customer_id: this.filters.customer_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getInvoices(data);

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.total.total_sum = result.data.total_sum;
      this.total.paid_count = result.data.paid_count;
      this.total.paid_sum = result.data.paid_sum;
      this.total.unpaid_count = result.data.unpaid_count;
      this.total.unpaid_sum = result.data.unpaid_sum;
      this.total.partial_count = result.data.partial_count;
      this.total.partial_sum = result.data.partial_sum;

      this.totalRows = result.data.total;
      this.items = result.data.invoices;
      this.updateSelected();
      this.isBusy = false;
    },
    // Modal
    showModalPayments(invoice) {
      if (this.$refs.invoicesPayments != null) {
        this.$refs.invoicesPayments.showPayments(invoice);
      }
    },
    onUpdateInvoicePayments() {
      this.getItems();
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item
      }

      const title = "Удаление счёта";
      const message = `Вы действительно хотите удалить счёт заказчику ${this.deleteRow.customer_name}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteInvoice(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Счёт удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Статус
    getStatusName(status_id) {
      const status = this.paidTypes.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 0:
          return 'tag-danger';
        case 1:
          return 'tag-success';
        case 2:
          return 'tag-warning';
        case 3:
          return 'tag-warning';
        default:
          return '';
      }
    },
    onInvoiceFile(invoice) {
      this.exportFile(invoice.id, "pdf", 1);
    },
    async exportFile(id, exportType, hasStamp) {
      const data = { id, exportType, hasStamp }

      this.loading = true;
      const result = await this.getInvoiceFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание файла. Попробуйте повторить позднее.");
        this.loading = false;
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
      this.loading = false;
    },
    onActFile(act) {
      this.exportActFile(act.id, "pdf", 1);
    },
    async exportActFile(id, exportType, hasStamp) {
      const data = {id, exportType, hasStamp}

      this.loading = true;
      const result = await this.getInvoiceActFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание PDF файла. Попробуйте повторить позднее.");
        this.loading = false;
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g, ' ');

      this.handleFileDownload(result.data, fileName);
      this.loading = false;
    },
    getFileName(disposition) {
      const defaultName = "untitled";

      if (!disposition) return defaultName;

      try {
        const { attachment, filename } = parse(disposition);
        return attachment ? filename : defaultName;
      } catch (_) {
        return defaultName;
      }
    },
    getTotalWorksSum(invoice) {
      return invoice.works.reduce((acc, item) => acc + item.sum, 0);
    },
    getTotalWorksSumText(invoice) {
      const sum = this.getTotalWorksSum(invoice);
      if (sum === 0) { return "0" }
      return this.capitalizeFirstLetter(rubles(sum));
    },
    capitalizeFirstLetter(val) {
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    },
    mapInvoiceForExport(item) {
      return {
        numb: item.numb || '',
        date: item.date ? this.formatTableDate(item.date) : '',
        customer_name: item.customer_name || '',
        organization_name: item.organization_name || '',
        paid_type: this.getStatusName(item.paid_type) || item.paid_type,
        acts: (item.acts && item.acts.length > 0)
          ? item.acts.map(actItem => `Акт №${actItem.numb}`).join(', ')
          : '',
        summa: this.priceFormat(item.summa) || item.summa,
        payment_sum: this.priceFormat(item.payment_sum) || item.payment_sum,
        remainder_sum: this.priceFormat(item.remainder_sum) || item.remainder_sum,
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.items] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
  }
}
</script>

<style>
#invoices td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Invoices/Invoices.vue